import html2canvas from "html2canvas";
import { setGoodSize, insertHtmlWithScripts } from './domUtils';
import { getDataAll } from './dataUtils';

/**
 * Универсальная функция для сохранения DOM-элементов как изображений
 * @param {Object} params
 * @param {HTMLElement|Array<string>} [params.container] - Контейнер для сохранения ИЛИ массив ID элементов (ИЛИ)
 * @param {HTMLElement} [params.elem] - Элемент с данными для запроса (ИЛИ)
 * @param {string} [params.url] - URL для запроса данных
 * @param {string} [params.selector='.toPrintMe, .card'] - Селектор элемента для сохранения
 * @param {string} [params.textSelector='.name-b'] - Селектор для setGoodSize
 * @param {number} [params.textSize] - Размер для setGoodSize
 * @param {string|Array<string>} [params.fileName] - Имя файла ИЛИ массив имен файлов
 * @param {Object} [params.data={}] - Дополнительные данные для запроса
 * @param {number} [params.delay=1000] - Задержка перед сохранением
 */
export function saveDomElement({
  container,
  elem,
  url,
  selector = null,
  textSelector = '.name-b',
  textSize,
  fileName,
  data = {},
  delay = null
}) {
  // Проверяем корректность параметров
  if (!container && selector && typeof selector === 'string') {
    container = document.querySelector(selector);
    if (!container) {
      throw new Error(`Элемент с селектором "${selector}" не найден`);
    }
  }
  if (container && url) {
    throw new Error('Нельзя одновременно передавать container и url. Выберите один вариант.');
  }

  if (!container && !url) {
    throw new Error('Необходимо передать либо container (для сохранения существующего элемента), либо url (для запроса данных).');
  }

  // Если передан контейнер - сохраняем его напрямую
  if (container) {
    // Проверяем, является ли container массивом ID

    if (Array.isArray(container)) {
      return saveElementsByIds({ elementIdentifiers: container, selector, textSelector, textSize, fileName, delay });
    }
    // Обычный контейнер
    return saveExistingElement({ container, selector, textSelector, textSize, fileName, delay });
  }

  // Если передан URL - делаем запрос
  if (url && elem) {
    return saveFromRequest(elem, url, selector, textSelector, textSize, fileName, data, delay);
  }

  throw new Error('Для запроса по URL необходимо передать elem с данными.');
}

// Функция для сохранения элементов по массиву ID
function saveElementsByIds({ elementIdentifiers, selector, textSelector, textSize, fileName, delay }) {
  const promises = elementIdentifiers.map((identifier, index) => {
    const element = document.querySelector(identifier);
    if (!element) {
      console.warn(`Элемент с ID "${identifier}" не найден`);
      return Promise.resolve(null);
    }

    // Генерируем имя файла для каждого элемента
    let currentFileName;
    if (Array.isArray(fileName)) {
      // Если fileName - массив, используем соответствующий элемент
      currentFileName = fileName[index] || `element_${identifier}_${Date.now()}.png`;
    } else if (fileName) {
      // Если fileName - строка, добавляем номер
      currentFileName = `${fileName.replace(/\.[^/.]+$/, '')}_${index + 1}.png`;
    } else {
      // Если fileName не задан, генерируем автоматически
      currentFileName = `element_${identifier}_${Date.now()}.png`;
    }

    // Используем существующую функцию для сохранения
    return new Promise((resolve) => {
      setTimeout(() => {
        saveExistingElement({
          container: element,
          selector,
          textSelector,
          textSize,
          fileName: currentFileName,
          delay: 0 // Задержку уже применяем здесь
        }).then(resolve).catch(error => {
          console.error(`Ошибка при сохранении элемента ${identifier}:`, error);
          resolve(null);
        });
      }, delay * index); // Задержка между сохранениями
    });
  });

  return Promise.all(promises);
}

// Функция для сохранения существующего элемента
function saveExistingElement({ container, selector, textSelector, textSize, fileName, delay }) {
  // Применяем размер к текстовым элементам если нужно
  if (textSize) {
    const textElements = container.querySelectorAll(textSelector);
    if (textElements.length > 0) {
      setGoodSize(textElements, textSize);
    }
  }

  // Находим элемент для сохранения
  const elementToSave = container.querySelector(selector) || container;

  // Генерируем изображение и скачиваем
  return new Promise((resolve) => {
    setTimeout(() => {
      html2canvas(elementToSave).then(canvas => {
        const defaultFileName = fileName || `element_${Date.now()}.png`;
        download(canvas, defaultFileName);
        resolve(canvas);
      }).catch(error => {
        console.error('Ошибка при создании изображения:', error);
        resolve(null);
      });
    }, delay);
  });
}

// Функция для сохранения после запроса
function saveFromRequest(elem, url, selector, textSelector, textSize, fileName, data, delay) {
  const dataSend = Object.assign(getDataAll(elem), data);

  return new SendData({ data: dataSend, url })
    .then((response) => {
      // Создаем временный контейнер
      const tempContainer = document.createElement('div');
      tempContainer.style.cssText = 'position: absolute; top: -9999px; left: -9999px;';

      // Вставляем HTML
      insertHtmlWithScripts({
        html: response.answer,
        parent: tempContainer,
        replaceParent: true
      });

      document.body.appendChild(tempContainer);

      // Применяем размер к текстовым элементам
      if (textSize) {
        const textElements = tempContainer.querySelectorAll(textSelector);
        if (textElements.length > 0) {
          setGoodSize(textElements, textSize);
        }
      }

      // Находим элемент для сохранения
      const elementToSave = tempContainer.querySelector(selector) || tempContainer;

      // Генерируем изображение и скачиваем
      return new Promise((resolve) => {
        setTimeout(() => {
          html2canvas(elementToSave).then(canvas => {
            const defaultFileName = fileName || `qr_${Date.now()}.png`;
            download(canvas, defaultFileName);
            tempContainer.remove(); // Удаляем временный контейнер
            resolve(canvas);
          }).catch(error => {
            console.error('Ошибка при создании изображения:', error);
            tempContainer.remove();
            resolve(null);
          });
        }, delay);
      });
    });
}

/**
 * Функция для скачивания canvas как изображения
 * @param {HTMLCanvasElement} canvas - Canvas элемент
 * @param {string} fileName - Имя файла для скачивания
 */
function download(canvas, fileName) {
  // Преобразуем canvas в blob
  canvas.toBlob((blob) => {
    // Создаем URL для blob
    const url = URL.createObjectURL(blob);

    // Создаем временную ссылку для скачивания
    const link = document.createElement('a');
    link.download = fileName;
    link.href = url;

    // Добавляем ссылку в DOM, кликаем и удаляем
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Освобождаем память
    URL.revokeObjectURL(url);
  }, 'image/png');
}